import React, { memo, useMemo } from "react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Legend,
} from "recharts";

/* Custom Tooltip */
const QueueTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
        <div
            style={{
                background: "#ffffff",
                padding: "10px 14px",
                borderRadius: 8,
                boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                border: "1px solid rgba(0,0,0,0.05)",
                minWidth: 160,
            }}
        >
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>

            {payload.map((p) => (
                <div key={p.dataKey} style={{ fontSize: 13 }}>
                    <span
                        style={{
                            display: "inline-block",
                            width: 10,
                            height: 10,
                            background: p.color,
                            borderRadius: "50%",
                            marginRight: 6,
                        }}
                    />
                    {p.name}: <strong>{p.value} mins</strong>
                </div>
            ))}
        </div>
    );
};
const MultiLineTick = ({ x, y, payload, width = 120 }) => {
    const words = payload.value.split(" ");
    const lines = [];
    let currentLine = "";

    words.forEach((word) => {
        if ((currentLine + word).length > 18) {
            lines.push(currentLine);
            currentLine = word;
        } else {
            currentLine = currentLine ? `${currentLine} ${word}` : word;
        }
    });

    if (currentLine) lines.push(currentLine);

    return (
        <g transform={`translate(${x},${y})`}>
            <text
                textAnchor="middle"
                fill="#374151"
                fontSize={12}
            >
                {lines.map((line, index) => (
                    <tspan
                        key={index}
                        x={0}
                        dy={index === 0 ? 12 : 14}
                    >
                        {line}
                    </tspan>
                ))}
            </text>
        </g>
    );
};

const QueueTimeChart = memo(function QueueTimeChart({ data }) {
    const chartData = useMemo(() => {
        return (data || []).map((item) => ({
            location: item.locationName,
            avgWait: item.averageWaitTime ?? 0,
            maxWait: item.maxWaitTime ?? 0,
        }));
    }, [data]);

    if (!chartData.length) return null;

    return (
        <ResponsiveContainer width="100%" height={600} debounce={300}>
            <BarChart
                data={chartData}
                margin={{
                    top: 60,
                    right: 40,
                    left: 70,   // space for Y-axis label
                    bottom: 120 // space for rotated X labels
                }}
                barGap={8}
            >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />

                {/* X Axis */}
                <XAxis
                    dataKey="location"
                    interval={0}
                    height={110}          // space for multiline labels
                    tick={<MultiLineTick />}
                />


                {/* Y Axis */}
                <YAxis
                    tick={{ fontSize: 12, fill: "#374151" }}
                    axisLine={{ stroke: "#9ca3af" }}
                    tickLine={false}
                    label={{
                        value: "Wait Time (mins)",
                        angle: -90,
                        position: "outsideLeft", //   FIXED
                        offset: 20,
                        fontSize: 13,
                        fontWeight: 600,
                        fill: "#374151",
                    }}
                />

                <Tooltip content={<QueueTooltip />} />

                {/* Legend on top */}
                <Legend
                    verticalAlign="top"
                    align="center"
                    wrapperStyle={{
                        paddingBottom: 20,
                        fontSize: 13,
                    }}
                />

                <Bar
                    dataKey="avgWait"
                    name="Average Wait Time"
                    fill="#60a5fa"
                    radius={[6, 6, 0, 0]}
                />

                <Bar
                    dataKey="maxWait"
                    name="Max Wait Time"
                    fill="#ef4444"
                    radius={[6, 6, 0, 0]}
                />
            </BarChart>
        </ResponsiveContainer>
    );
});

export default QueueTimeChart;
